/**
 * Per-polygon import of BUDOVA ↔ ROZVADEC links (návaznosti).
 * One nested JSON: { rozvadece: [{ id?, nazev?, budovy: [{ id|ropid|adresa|parcela|cp }] }] }.
 * Lookup is scoped to one polygon; first unique fallback wins; 0 or >1 matches is an error.
 */

import { normalizeAcquisitionText } from '../constants/acquisition-report.constants';
import { normalizeRopId } from '../constants/ropid-import.constants';
import { formatBudovaAddressLabel, formatBudovaCpLabel } from './budova-address-label.util';
import { formatRozvadecLabel } from './rozvadec-label.util';

export const NAVAZNOST_JSON_EXAMPLE = `{
  "rozvadece": [
    {
      "id": "mrc78lh47knuzpgpl",
      "nazev": "RSU 12",
      "budovy": [
        { "id": "mrc78lh2iqjvjtqdj" },
        { "ropid": "1485410" },
        { "adresa": "Na Návsi, 41301 Račiněves" }
      ]
    },
    {
      "nazev": "RSU jih",
      "budovy": [
        { "parcela": "3973/3" },
        { "cp": "12" }
      ]
    }
  ]
}`;

export type NavaznostRozvadecMatchedBy = 'id' | 'name' | 'manual';
export type NavaznostBudovaMatchedBy = 'objectId' | 'ropid' | 'address' | 'parcelNumber' | 'cp' | 'manual';

export interface NavaznostManualAssignment {
  rozvadecId?: string;
  objectId?: string;
}

/** Keys = preview `rowIndex` (JSON body uses string keys). */
export type NavaznostManualAssignments = Record<number, NavaznostManualAssignment>;

export interface NavaznostImportRequestBody {
  rozvadece: unknown;
  manualAssignments?: NavaznostManualAssignments | Record<string, NavaznostManualAssignment>;
}

export interface NavaznostLinkInput {
  rozvadecId?: string;
  rozvadecName?: string;
  objectId?: string;
  ropid?: string;
  address?: string;
  parcelNumber?: string;
  cp?: string;
}

export interface NavaznostCabinetRecord {
  id: string;
  name?: string | null;
}

export interface NavaznostBuildingRecord {
  id: string;
  ropid?: string | null;
  parcelNumber?: string | null;
  cisloPopisne?: string | null;
  /** `P` / `E` z address_points; chybí → formátovač použije P. */
  typCisla?: string | null;
  orientacni?: string | null;
  ulice?: string | null;
  obec?: string | null;
  psc?: string | null;
  acquisitionCisloPopisne?: string | null;
  acquisitionTypCisla?: string | null;
  acquisitionUlice?: string | null;
  acquisitionMesto?: string | null;
  acquisitionParcelNumber?: string | null;
  extraAddress?: string | null;
  previousRozvadecId?: string | null;
  previousRozvadecName?: string | null;
}

export type NavaznostRowStatus = 'matched' | 'unmatched' | 'ambiguous' | 'invalid';

export interface NavaznostMatchResult {
  rowIndex: number;
  input: NavaznostLinkInput;
  status: NavaznostRowStatus;
  rozvadecId: string | null;
  rozvadecName: string | null;
  rozvadecMatchedBy: NavaznostRozvadecMatchedBy | null;
  objectId: string | null;
  objectMatchedBy: NavaznostBudovaMatchedBy | null;
  previousRozvadecId: string | null;
  previousRozvadecName: string | null;
  error: string | null;
}

export interface NavaznostLookups {
  cabinetsById: Map<string, NavaznostCabinetRecord>;
  cabinetsByName: Map<string, string[]>;
  buildingsById: Map<string, NavaznostBuildingRecord>;
  buildingsByRopid: Map<string, string[]>;
  buildingsByAddress: Map<string, string[]>;
  buildingsByParcel: Map<string, string[]>;
  buildingsByCp: Map<string, string[]>;
}

function textField(row: Record<string, unknown>, key: string): string {
  if (!Object.prototype.hasOwnProperty.call(row, key)) return '';
  const raw = row[key];
  if (raw == null || raw === '') return '';
  const text = String(raw).trim();
  return text;
}

export function normalizeNavaznostAddressKey(value: string | null | undefined): string {
  return normalizeAcquisitionText(value)
    .replace(/\b[cč]\.?\s*p\.?\b/gi, ' ')
    .replace(/[.,;:()/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeNavaznostParcelKey(value: string | null | undefined): string {
  return normalizeAcquisitionText(value)
    .replace(/\bpoz\.?\b/g, '')
    .replace(/\bparc\.?\b/g, '')
    .replace(/\bc\.?\b/g, '')
    .replace(/\s+/g, '')
    .trim();
}

export function normalizeNavaznostCpKey(value: string | null | undefined): string {
  return normalizeAcquisitionText(value)
    .replace(/\b[cč]\.?\s*p\.?\b/gi, '')
    .replace(/[^a-z0-9/]/g, '');
}

function pushId(map: Map<string, string[]>, key: string, id: string): void {
  if (!key) return;
  const list = map.get(key) ?? [];
  if (!list.includes(id)) list.push(id);
  map.set(key, list);
}

function pickUnique(
  map: Map<string, string[]>,
  key: string,
): { kind: 'hit'; id: string } | { kind: 'miss' } | { kind: 'ambiguous'; ids: string[] } {
  if (!key) return { kind: 'miss' };
  const ids = map.get(key) ?? [];
  if (ids.length === 1) return { kind: 'hit', id: ids[0] };
  if (ids.length > 1) return { kind: 'ambiguous', ids };
  return { kind: 'miss' };
}

function buildingAddressKeys(building: NavaznostBuildingRecord): string[] {
  const keys = new Set<string>();
  const add = (value: string | null | undefined) => {
    const key = normalizeNavaznostAddressKey(value);
    if (key) keys.add(key);
  };
  add(
    formatBudovaAddressLabel({
      addressPoint: {
        cisloPopisne: building.cisloPopisne,
        typCisla: building.typCisla,
        orientacni: building.orientacni,
        ulice: building.ulice,
        obec: building.obec,
        psc: building.psc,
      },
      acquisitionAddress: {
        cisloPopisne: building.acquisitionCisloPopisne,
        typCisla: building.acquisitionTypCisla,
        ulice: building.acquisitionUlice,
        mesto: building.acquisitionMesto,
        parcelNumber: building.acquisitionParcelNumber,
      },
      parcelNumber: building.parcelNumber,
    }),
  );
  add(
    formatBudovaAddressLabel(
      {
        addressPoint: {
          cisloPopisne: building.cisloPopisne,
          typCisla: building.typCisla,
          orientacni: building.orientacni,
          ulice: building.ulice,
          obec: building.obec,
          psc: building.psc,
        },
        acquisitionAddress: {
          cisloPopisne: building.acquisitionCisloPopisne,
          typCisla: building.acquisitionTypCisla,
          ulice: building.acquisitionUlice,
          mesto: building.acquisitionMesto,
          parcelNumber: building.acquisitionParcelNumber,
        },
        parcelNumber: building.parcelNumber,
      },
      { omitCp: true },
    ),
  );
  add([building.ulice, building.obec].filter(Boolean).join(' '));
  add([building.acquisitionUlice, building.acquisitionMesto].filter(Boolean).join(' '));
  add(building.extraAddress);
  return [...keys];
}

function buildingCpKeys(building: NavaznostBuildingRecord): string[] {
  const keys = new Set<string>();
  const add = (value: string | null | undefined) => {
    const key = normalizeNavaznostCpKey(value);
    if (key) keys.add(key);
  };
  add(building.cisloPopisne);
  add(building.acquisitionCisloPopisne);
  add(
    formatBudovaCpLabel({
      addressPoint: {
        cisloPopisne: building.cisloPopisne,
        typCisla: building.typCisla,
        orientacni: building.orientacni,
      },
      acquisitionAddress: {
        cisloPopisne: building.acquisitionCisloPopisne,
        typCisla: building.acquisitionTypCisla,
      },
    }),
  );
  return [...keys];
}

function compactNavaznostLink(link: NavaznostLinkInput): NavaznostLinkInput {
  const out: NavaznostLinkInput = {};
  if (link.rozvadecId) out.rozvadecId = link.rozvadecId;
  if (link.rozvadecName) out.rozvadecName = link.rozvadecName;
  if (link.objectId) out.objectId = link.objectId;
  if (link.ropid) out.ropid = link.ropid;
  if (link.address) out.address = link.address;
  if (link.parcelNumber) out.parcelNumber = link.parcelNumber;
  if (link.cp) out.cp = link.cp;
  return out;
}

function parseBuildingInput(raw: unknown, rowIndex: number): NavaznostLinkInput {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error(`Budova ${rowIndex + 1}: očekáván objekt (id, ropid, adresa, parcela nebo cp).`);
  }
  const row = raw as Record<string, unknown>;
  const building = compactNavaznostLink({
    objectId: textField(row, 'id') || undefined,
    ropid: textField(row, 'ropid') || undefined,
    address: textField(row, 'adresa') || undefined,
    parcelNumber: textField(row, 'parcela') || undefined,
    cp: textField(row, 'cp') || undefined,
  });
  if (!building.objectId && !building.ropid && !building.address && !building.parcelNumber && !building.cp) {
    throw new Error(`Budova ${rowIndex + 1}: uveďte id, ropid, adresa, parcela nebo cp.`);
  }
  return building;
}

export function parseBudovaRozvadecNavaznostPayload(raw: unknown): NavaznostLinkInput[] {
  let data: unknown = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      throw new Error('JSON se nepodařilo načíst.');
    }
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('JSON musí být objekt { "rozvadece": [ ... ] }.');
  }
  const list = (data as Record<string, unknown>)['rozvadece'];
  if (!Array.isArray(list) || list.length === 0) {
    throw new Error('JSON musí obsahovat neprázdné pole rozvadece.');
  }

  const rows: NavaznostLinkInput[] = [];
  for (let index = 0; index < list.length; index++) {
    const item = list[index];
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      throw new Error(`Rozvaděč ${index + 1}: očekáván objekt s polem budovy.`);
    }
    const block = item as Record<string, unknown>;
    const budovy = block['budovy'];
    if (!Array.isArray(budovy) || budovy.length === 0) {
      throw new Error(`Rozvaděč ${index + 1}: chybí neprázdné pole budovy.`);
    }
    const cabinet = compactNavaznostLink({
      rozvadecId: textField(block, 'id') || undefined,
      rozvadecName: textField(block, 'nazev') || undefined,
    });
    if (!cabinet.rozvadecId && !cabinet.rozvadecName) {
      throw new Error(`Rozvaděč ${index + 1}: uveďte id nebo nazev.`);
    }
    for (const buildingRaw of budovy) {
      const building = parseBuildingInput(buildingRaw, rows.length);
      rows.push(compactNavaznostLink({ ...building, ...cabinet }));
    }
  }
  return rows;
}

export function buildNavaznostLookups(
  cabinets: NavaznostCabinetRecord[],
  buildings: NavaznostBuildingRecord[],
): NavaznostLookups {
  const cabinetsById = new Map<string, NavaznostCabinetRecord>();
  const cabinetsByName = new Map<string, string[]>();
  for (const cabinet of cabinets) {
    const id = cabinet.id?.trim();
    if (!id) continue;
    cabinetsById.set(id, { id, name: cabinet.name ?? null });
    const label = formatRozvadecLabel({ id, name: cabinet.name });
    const nameKey = normalizeAcquisitionText(label);
    if (nameKey && nameKey !== normalizeAcquisitionText(id)) {
      pushId(cabinetsByName, nameKey, id);
    }
    const rawName = normalizeAcquisitionText(cabinet.name);
    if (rawName && rawName !== normalizeAcquisitionText(id)) {
      pushId(cabinetsByName, rawName, id);
    }
  }

  const buildingsById = new Map<string, NavaznostBuildingRecord>();
  const buildingsByRopid = new Map<string, string[]>();
  const buildingsByAddress = new Map<string, string[]>();
  const buildingsByParcel = new Map<string, string[]>();
  const buildingsByCp = new Map<string, string[]>();
  for (const building of buildings) {
    const id = building.id?.trim();
    if (!id) continue;
    buildingsById.set(id, building);
    pushId(buildingsByRopid, normalizeRopId(building.ropid), id);
    for (const key of buildingAddressKeys(building)) pushId(buildingsByAddress, key, id);
    pushId(buildingsByParcel, normalizeNavaznostParcelKey(building.parcelNumber), id);
    pushId(buildingsByParcel, normalizeNavaznostParcelKey(building.acquisitionParcelNumber), id);
    for (const key of buildingCpKeys(building)) pushId(buildingsByCp, key, id);
  }

  return {
    cabinetsById,
    cabinetsByName,
    buildingsById,
    buildingsByRopid,
    buildingsByAddress,
    buildingsByParcel,
    buildingsByCp,
  };
}

function matchCabinet(
  input: NavaznostLinkInput,
  lookups: NavaznostLookups,
): { id: string; name: string | null; matchedBy: NavaznostRozvadecMatchedBy } | { error: string; status: NavaznostRowStatus } {
  const id = (input.rozvadecId ?? '').trim();
  if (id) {
    const hit = lookups.cabinetsById.get(id);
    if (hit) {
      return {
        id: hit.id,
        name: formatRozvadecLabel(hit) || hit.name || hit.id,
        matchedBy: 'id',
      };
    }
  }

  const nameKey = normalizeAcquisitionText(input.rozvadecName);
  if (nameKey) {
    const byName = pickUnique(lookups.cabinetsByName, nameKey);
    if (byName.kind === 'hit') {
      const hit = lookups.cabinetsById.get(byName.id);
      if (hit) {
        return {
          id: hit.id,
          name: formatRozvadecLabel(hit) || hit.name || hit.id,
          matchedBy: 'name',
        };
      }
    }
    if (byName.kind === 'ambiguous') {
      return {
        status: 'ambiguous',
        error: `Rozvaděč: název není jednoznačný (${byName.ids.length} shod v polygonu).`,
      };
    }
  }

  if (!id && !nameKey) {
    return { status: 'invalid', error: 'Chybí rozvaděč (id nebo nazev).' };
  }
  return { status: 'unmatched', error: 'Rozvaděč v tomto polygonu nenalezen.' };
}

function matchBuilding(
  input: NavaznostLinkInput,
  lookups: NavaznostLookups,
): { id: string; matchedBy: NavaznostBudovaMatchedBy } | { error: string; status: NavaznostRowStatus } {
  const steps: Array<{ value: string; matchedBy: NavaznostBudovaMatchedBy; pick: ReturnType<typeof pickUnique> }> = [
    {
      value: (input.objectId ?? '').trim(),
      matchedBy: 'objectId',
      pick: (input.objectId ?? '').trim()
        ? lookups.buildingsById.has((input.objectId ?? '').trim())
          ? { kind: 'hit', id: (input.objectId ?? '').trim() }
          : { kind: 'miss' }
        : { kind: 'miss' },
    },
    {
      value: (input.ropid ?? '').trim(),
      matchedBy: 'ropid',
      pick: pickUnique(lookups.buildingsByRopid, normalizeRopId(input.ropid)),
    },
    {
      value: (input.address ?? '').trim(),
      matchedBy: 'address',
      pick: pickUnique(lookups.buildingsByAddress, normalizeNavaznostAddressKey(input.address)),
    },
    {
      value: (input.parcelNumber ?? '').trim(),
      matchedBy: 'parcelNumber',
      pick: pickUnique(lookups.buildingsByParcel, normalizeNavaznostParcelKey(input.parcelNumber)),
    },
    {
      value: (input.cp ?? '').trim(),
      matchedBy: 'cp',
      pick: pickUnique(lookups.buildingsByCp, normalizeNavaznostCpKey(input.cp)),
    },
  ];

  const hasAny = steps.some((step) => step.value);
  if (!hasAny) {
    return { status: 'invalid', error: 'Chybí budova (id, ropid, adresa, parcela nebo cp).' };
  }

  for (const step of steps) {
    if (!step.value) continue;
    if (step.pick.kind === 'hit') {
      return { id: step.pick.id, matchedBy: step.matchedBy };
    }
    if (step.pick.kind === 'ambiguous') {
      const label =
        step.matchedBy === 'objectId'
          ? 'id'
          : step.matchedBy === 'ropid'
            ? 'ROPID'
            : step.matchedBy === 'address'
              ? 'adresa'
              : step.matchedBy === 'parcelNumber'
                ? 'parcela'
                : 'č.p.';
      return {
        status: 'ambiguous',
        error: `Budova: ${label} není jednoznačné (${step.pick.ids.length} shod v polygonu).`,
      };
    }
  }

  return { status: 'unmatched', error: 'Budova v tomto polygonu nenalezena.' };
}

export function matchBudovaRozvadecNavaznostRow(
  input: NavaznostLinkInput,
  rowIndex: number,
  lookups: NavaznostLookups,
  usedBuildingIds?: Set<string>,
): NavaznostMatchResult {
  const empty: NavaznostMatchResult = {
    rowIndex,
    input,
    status: 'invalid',
    rozvadecId: null,
    rozvadecName: null,
    rozvadecMatchedBy: null,
    objectId: null,
    objectMatchedBy: null,
    previousRozvadecId: null,
    previousRozvadecName: null,
    error: null,
  };

  const cabinet = matchCabinet(input, lookups);
  if ('error' in cabinet) {
    return { ...empty, status: cabinet.status, error: cabinet.error };
  }

  const building = matchBuilding(input, lookups);
  if ('error' in building) {
    return {
      ...empty,
      status: building.status,
      rozvadecId: cabinet.id,
      rozvadecName: cabinet.name,
      rozvadecMatchedBy: cabinet.matchedBy,
      error: building.error,
    };
  }

  const record = lookups.buildingsById.get(building.id);
  if (usedBuildingIds?.has(building.id)) {
    return {
      ...empty,
      status: 'invalid',
      rozvadecId: cabinet.id,
      rozvadecName: cabinet.name,
      rozvadecMatchedBy: cabinet.matchedBy,
      objectId: building.id,
      objectMatchedBy: building.matchedBy,
      previousRozvadecId: record?.previousRozvadecId ?? null,
      previousRozvadecName: record?.previousRozvadecName ?? null,
      error: 'Budova je v souboru přiřazena víckrát.',
    };
  }
  usedBuildingIds?.add(building.id);

  return {
    rowIndex,
    input,
    status: 'matched',
    rozvadecId: cabinet.id,
    rozvadecName: cabinet.name,
    rozvadecMatchedBy: cabinet.matchedBy,
    objectId: building.id,
    objectMatchedBy: building.matchedBy,
    previousRozvadecId: record?.previousRozvadecId ?? null,
    previousRozvadecName: record?.previousRozvadecName ?? null,
    error: null,
  };
}

export function matchBudovaRozvadecNavaznostRows(
  rows: NavaznostLinkInput[],
  lookups: NavaznostLookups,
): NavaznostMatchResult[] {
  const used = new Set<string>();
  return rows.map((row, index) => matchBudovaRozvadecNavaznostRow(row, index, lookups, used));
}

export function navaznostMatchedByLabel(
  matchedBy: NavaznostRozvadecMatchedBy | NavaznostBudovaMatchedBy | null,
): string {
  switch (matchedBy) {
    case 'id':
    case 'objectId':
      return 'id';
    case 'name':
      return 'název';
    case 'manual':
      return 'ručně';
    case 'ropid':
      return 'ROPID';
    case 'address':
      return 'adresa';
    case 'parcelNumber':
      return 'parcela';
    case 'cp':
      return 'č.p.';
    default:
      return '—';
  }
}

export function parseNavaznostManualAssignments(raw: unknown): NavaznostManualAssignments {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: NavaznostManualAssignments = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const rowIndex = Number(key);
    if (!Number.isInteger(rowIndex) || rowIndex < 0) continue;
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const row = value as Record<string, unknown>;
    const assignment: NavaznostManualAssignment = {};
    if (typeof row['rozvadecId'] === 'string' && row['rozvadecId'].trim()) {
      assignment.rozvadecId = row['rozvadecId'].trim();
    }
    if (typeof row['objectId'] === 'string' && row['objectId'].trim()) {
      assignment.objectId = row['objectId'].trim();
    }
    if (assignment.rozvadecId || assignment.objectId) {
      out[rowIndex] = assignment;
    }
  }
  return out;
}

function assignmentForRow(
  assignments: NavaznostManualAssignments | undefined,
  rowIndex: number,
): NavaznostManualAssignment | undefined {
  if (!assignments) return undefined;
  return assignments[rowIndex] ?? (assignments as Record<string, NavaznostManualAssignment>)[String(rowIndex)];
}

function applyNavaznostManualAssignmentToRow(
  row: NavaznostMatchResult,
  assignment: NavaznostManualAssignment | undefined,
  lookups: NavaznostLookups,
): NavaznostMatchResult {
  if (!assignment) return { ...row };

  let next: NavaznostMatchResult = { ...row, error: null };

  if (assignment.rozvadecId) {
    const hit = lookups.cabinetsById.get(assignment.rozvadecId);
    if (!hit) {
      return {
        ...next,
        status: 'invalid',
        rozvadecId: null,
        rozvadecName: null,
        rozvadecMatchedBy: 'manual',
        error: 'Ručně zvolený rozvaděč není v tomto polygonu.',
      };
    }
    next = {
      ...next,
      rozvadecId: hit.id,
      rozvadecName: formatRozvadecLabel(hit) || hit.name || hit.id,
      rozvadecMatchedBy: 'manual',
    };
  }

  if (assignment.objectId) {
    const hit = lookups.buildingsById.get(assignment.objectId);
    if (!hit) {
      return {
        ...next,
        status: 'invalid',
        objectId: null,
        objectMatchedBy: 'manual',
        error: 'Ručně zvolená budova není v tomto polygonu.',
      };
    }
    next = {
      ...next,
      objectId: hit.id,
      objectMatchedBy: 'manual',
      previousRozvadecId: hit.previousRozvadecId ?? null,
      previousRozvadecName: hit.previousRozvadecName ?? null,
    };
  }

  if (!next.rozvadecId) {
    const cabinet = matchCabinet(next.input, lookups);
    if (!('error' in cabinet)) {
      next = {
        ...next,
        rozvadecId: cabinet.id,
        rozvadecName: cabinet.name,
        rozvadecMatchedBy: cabinet.matchedBy,
      };
    }
  }

  if (!next.objectId) {
    const building = matchBuilding(next.input, lookups);
    if (!('error' in building)) {
      const record = lookups.buildingsById.get(building.id);
      next = {
        ...next,
        objectId: building.id,
        objectMatchedBy: building.matchedBy,
        previousRozvadecId: record?.previousRozvadecId ?? null,
        previousRozvadecName: record?.previousRozvadecName ?? null,
      };
    }
  }

  if (next.rozvadecId && next.objectId && next.status !== 'invalid') {
    next = { ...next, status: 'matched', error: null };
  } else if (next.status !== 'invalid' && (!next.rozvadecId || !next.objectId)) {
    next = {
      ...next,
      status: next.rozvadecId || next.objectId ? next.status : 'unmatched',
      error: next.error ?? 'Doplňte chybějící rozvaděč nebo budovu.',
    };
  }

  return next;
}

function reconcileNavaznostDuplicateBuildings(rows: NavaznostMatchResult[]): NavaznostMatchResult[] {
  const used = new Set<string>();
  return rows.map((row) => {
    if (row.status !== 'matched' || !row.objectId) return row;
    if (used.has(row.objectId)) {
      return {
        ...row,
        status: 'invalid',
        error: 'Budova je v souboru přiřazena víckrát.',
      };
    }
    used.add(row.objectId);
    return row;
  });
}

/** Auto-match preview with optional manual picks (manual wins over auto for set fields). */
export function applyNavaznostManualAssignments(
  preview: NavaznostMatchResult[],
  assignments: NavaznostManualAssignments | undefined,
  lookups: NavaznostLookups,
): NavaznostMatchResult[] {
  if (!assignments || Object.keys(assignments).length === 0) {
    return preview;
  }
  const draft = preview.map((row) =>
    applyNavaznostManualAssignmentToRow(row, assignmentForRow(assignments, row.rowIndex), lookups),
  );
  return reconcileNavaznostDuplicateBuildings(draft);
}

export function summarizeNavaznostMatchPreview(preview: NavaznostMatchResult[]): {
  total: number;
  matched: number;
  unmatched: number;
  ambiguous: number;
  invalid: number;
} {
  return {
    total: preview.length,
    matched: preview.filter((row) => row.status === 'matched').length,
    unmatched: preview.filter((row) => row.status === 'unmatched').length,
    ambiguous: preview.filter((row) => row.status === 'ambiguous').length,
    invalid: preview.filter((row) => row.status === 'invalid').length,
  };
}
